import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, type Project, type Category, type ListMeta } from "../lib/api";
import { ProjectCard } from "../components/ProjectCard";
import { usePageTitle } from "../lib/pageTitle";
import "./PortfolioPage.scss";

export function PortfolioPage() {
  const { viewSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [meta, setMeta] = useState<ListMeta | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const q = searchParams.get("q") || "";
  const category = searchParams.get("category") || "";
  const tag = searchParams.get("tag") || "";
  const sort = searchParams.get("sort") || "priority";
  const page = parseInt(searchParams.get("page") || "1", 10) || 1;

  useEffect(() => {
    api.categories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .projects({
        q: q || undefined,
        category: category || undefined,
        tag: tag || undefined,
        sort,
        page,
        perPage: 24,
        view: viewSlug || undefined,
      })
      .then((res) => {
        setProjects(res.data);
        setMeta(res.meta);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q, category, tag, sort, page, viewSlug]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  }

  const mode = meta?.mode;
  const title =
    meta?.view?.name ||
    meta?.category?.name ||
    (category ? categories.find((c) => c.slug === category)?.name : null) ||
    "Portfolio";
  const description =
    meta?.view?.description ||
    meta?.category?.description ||
    (mode === "category"
      ? `Only projects tagged ${title}.`
      : "Browse projects by category, tag, or search. Share tailored views with clients and collaborators.");

  const tabTitle = viewSlug
    ? meta?.view?.name || meta?.category?.name || viewSlug
    : category
      ? categories.find((c) => c.slug === category)?.name || category
      : q
        ? `Search: ${q}`
        : "Portfolio";
  usePageTitle(tabTitle);

  const activeSlug = viewSlug || category;
  const isCategoryMode = mode === "category" || (!!viewSlug && categories.some((c) => c.slug === viewSlug));
  const isViewMode = mode === "view";

  return (
    <div className="portfolio page-enter container">
      <header className="portfolio__header">
        <div>
          <p className="portfolio__eyebrow">Work</p>
          <h1>{title}</h1>
          <p className="portfolio__desc">{description}</p>
          {activeSlug && (
            <p className="portfolio__view-note">
              {isCategoryMode && !isViewMode ? (
                <>
                  Filtered by category <code>/portfolio/{activeSlug}</code>
                </>
              ) : (
                <>
                  Viewing curated list <code>/portfolio/{activeSlug}</code>
                </>
              )}
              {" · "}
              <Link to="/portfolio">show all</Link>
            </p>
          )}
        </div>
      </header>

      <div className="portfolio__filters">
        <input
          type="search"
          placeholder="Search projects…"
          defaultValue={q}
          onKeyDown={(e) => {
            if (e.key === "Enter") updateParam("q", (e.target as HTMLInputElement).value);
          }}
          onBlur={(e) => updateParam("q", e.target.value)}
          aria-label="Search projects"
        />
        <select
          value={viewSlug && isCategoryMode ? viewSlug : category}
          onChange={(e) => {
            const slug = e.target.value;
            if (!slug) {
              // clear path filter → all portfolio
              window.location.href = "/portfolio";
              return;
            }
            // Category hard-filter via path
            window.location.href = `/portfolio/${slug}`;
          }}
          aria-label="Filter by category"
          disabled={isViewMode}
        >
          <option value="">All categories</option>
          {categories
            .filter((c) => typeof c.projectCount !== "number" || c.projectCount > 0)
            .map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
                {typeof c.projectCount === "number" ? ` (${c.projectCount})` : ""}
              </option>
            ))}
        </select>
        <select
          value={sort}
          onChange={(e) => updateParam("sort", e.target.value)}
          aria-label="Sort"
        >
          <option value="priority">Priority</option>
          <option value="date">Date</option>
          <option value="title">Alphabetical</option>
        </select>
      </div>

      <div className="portfolio__quick">
        {categories
          .filter((c) => typeof c.projectCount !== "number" || c.projectCount > 0)
          .map((c) => {
            const countLabel =
              typeof c.projectCount === "number"
                ? `${c.projectCount} project${c.projectCount === 1 ? "" : "s"}`
                : undefined;
            return (
              <Link
                key={c.id}
                to={`/portfolio/${c.slug}`}
                className={viewSlug === c.slug || category === c.slug ? "is-active" : ""}
                aria-label={countLabel ? `${c.name}, ${countLabel}` : c.name}
              >
                {c.name}
                {typeof c.projectCount === "number" ? (
                  <span aria-hidden="true">{` (${c.projectCount})`}</span>
                ) : null}
              </Link>
            );
          })}
      </div>

      {loading && <p className="portfolio__status">Loading…</p>}
      {error && <p className="portfolio__status portfolio__status--err">{error}</p>}

      {!loading && !error && projects.length === 0 && (
        <p className="portfolio__status">
          {isCategoryMode
            ? `No published projects in ${title} yet.`
            : "No published projects match these filters."}
        </p>
      )}

      <div className="project-grid">
        {projects.map((p, i) => (
          <ProjectCard key={p.id} project={p} index={i} />
        ))}
      </div>

      {meta && meta.totalPages > 1 && (
        <div className="portfolio__pager">
          <button
            className="btn btn--ghost btn--sm"
            disabled={page <= 1}
            onClick={() => updateParam("page", String(page - 1))}
          >
            Previous
          </button>
          <span>
            Page {meta.page} of {meta.totalPages} · {meta.total} projects
          </span>
          <button
            className="btn btn--ghost btn--sm"
            disabled={page >= meta.totalPages}
            onClick={() => updateParam("page", String(page + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
